import { useCallback, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { useDismiss } from '../lib/use-dismiss';
import { useMessages } from '../i18n/locale';
import {
  PERIOD_PRESETS,
  monthGrid,
  periodDays,
  presetOf,
  presetPeriod,
  sameDay,
  startOfDay,
  type Period,
  type PeriodPreset,
} from '../lib/period';
import { pillStyle } from './primitives';

/**
 * Пилюля периода с календарём, как в панели Zenta: слева месяц, справа быстрые
 * периоды, внизу — выбранный диапазон и «Сбросить».
 *
 * Диапазон выбирается двумя щелчками: первый — начало (и сразу однодневный период),
 * второй — конец. Будущие дни недоступны: заказов из будущего в ленте нет.
 */
export function PeriodPicker({
  value,
  onChange,
  presets = PERIOD_PRESETS,
  emptyLabel,
}: {
  readonly value: Period | null;
  readonly onChange: (next: Period | null) => void;
  /** Быстрые периоды справа; по умолчанию — как в ленте. */
  readonly presets?: readonly PeriodPreset[];
  /**
   * Подпись пилюли, пока выбран быстрый период, показанный рядом отдельной пилюлей
   * («Выбрать даты» в журнале). Без неё пилюля называет выбранный период.
   */
  readonly emptyLabel?: string;
}): ReactElement {
  const messages = useMessages();
  const labels = messages.cabinet.period;
  const now = new Date();
  const today = startOfDay(now);

  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => monthOf(value?.to ?? today));
  // Начало диапазона, ждущее второго щелчка.
  const [anchor, setAnchor] = useState<Date | null>(null);
  const root = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setAnchor(null);
  }, []);

  useDismiss(open, close, root);

  // Названия месяцев и дней — из ресурсов, а не из `Intl`: в браузерах без данных
  // узбекской локали `Intl` отдаёт «M11» вместо «noyabr».
  const dayMonth = (day: Date): string =>
    labels.dayMonth
      .replace('{day}', String(day.getDate()))
      .replace('{month}', labels.monthsOf[day.getMonth()] ?? '');

  const range = (period: Period): string => {
    if (sameDay(period.from, period.to)) {
      return dayMonth(period.to);
    }

    const sameMonth =
      period.from.getMonth() === period.to.getMonth() &&
      period.from.getFullYear() === period.to.getFullYear();

    return `${sameMonth ? period.from.getDate() : dayMonth(period.from)} — ${dayMonth(period.to)}`;
  };

  const preset = presetOf(value, now, presets);
  const custom = preset === null;
  const pillLabel =
    emptyLabel !== undefined && !custom
      ? emptyLabel
      : preset !== null && preset !== 'last7'
        ? labels[preset]
        : value === null
          ? labels.all
          : range(value);

  const pick = (day: Date): void => {
    if (anchor === null) {
      setAnchor(day);
      onChange({ from: day, to: day });

      return;
    }

    onChange(anchor <= day ? { from: anchor, to: day } : { from: day, to: anchor });
    setAnchor(null);
  };

  const choose = (next: PeriodPreset): void => {
    onChange(presetPeriod(next, now));
    setAnchor(null);
    setOpen(false);
  };

  const atCurrentMonth =
    month.getFullYear() === today.getFullYear() && month.getMonth() === today.getMonth();

  return (
    <div ref={root} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-expanded={open}
        aria-label={labels.label}
        onClick={() => setOpen((current) => !current)}
        style={{
          ...pillStyle,
          background: emptyLabel !== undefined && custom ? 'var(--accent)' : 'var(--surface)',
          borderColor: emptyLabel !== undefined && custom ? 'var(--accent)' : 'var(--line)',
          color: emptyLabel !== undefined && custom ? 'var(--text-on-accent)' : 'var(--text)',
        }}
      >
        <CalendarIcon />
        {pillLabel}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={labels.label}
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--sp-2))',
            left: 0,
            zIndex: 20,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-lg)',
            boxShadow: 'var(--shadow-pop)',
            padding: 'var(--sp-4)',
            display: 'grid',
            gap: 'var(--sp-3)',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--sp-4)', alignItems: 'flex-start' }}>
            <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <button
                  type="button"
                  aria-label={labels.previousMonth}
                  onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                  style={arrowStyle(false)}
                >
                  ‹
                </button>
                <b style={{ fontSize: 'var(--fs-lg)' }}>
                  {`${labels.months[month.getMonth()] ?? ''} ${month.getFullYear()}`}
                </b>
                <button
                  type="button"
                  aria-label={labels.nextMonth}
                  disabled={atCurrentMonth}
                  onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                  style={arrowStyle(atCurrentMonth)}
                >
                  ›
                </button>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, var(--cal-cell))',
                  gap: 'var(--sp-1)',
                }}
              >
                {labels.weekdays.map((name) => (
                  <span
                    key={name}
                    className="caption"
                    style={{ textAlign: 'center', lineHeight: 'var(--cal-cell)' }}
                  >
                    {name}
                  </span>
                ))}

                {monthGrid(month).map((day) => {
                  const future = day > today;
                  const inMonth = day.getMonth() === month.getMonth();
                  const edge =
                    value !== null && (sameDay(day, value.from) || sameDay(day, value.to));
                  const inside = value !== null && day > value.from && day < value.to;

                  return (
                    <button
                      key={day.getTime()}
                      type="button"
                      disabled={future}
                      aria-pressed={edge || inside}
                      aria-current={sameDay(day, today) ? 'date' : undefined}
                      onClick={() => pick(day)}
                      style={dayStyle({
                        edge,
                        inside,
                        inMonth,
                        future,
                        today: sameDay(day, today),
                      })}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'grid', gap: 'var(--sp-1)', minWidth: '180px' }}>
              {presets.map((code) => {
                const period = presetPeriod(code, now);

                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => choose(code)}
                    style={{
                      display: 'grid',
                      textAlign: 'left',
                      border: 'none',
                      borderRadius: 'var(--r-sm)',
                      padding: 'var(--sp-2) var(--sp-3)',
                      background: preset === code ? 'var(--accent-soft)' : 'transparent',
                      color: preset === code ? 'var(--accent-ink)' : 'var(--text)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--fs)',
                        fontWeight: preset === code ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                      }}
                    >
                      {code === 'last7' ? messages.cabinet.journal.last7 : labels[code]}
                    </span>
                    {period === null ? null : <span className="caption">{range(period)}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: '1px solid var(--line)',
              paddingTop: 'var(--sp-3)',
            }}
          >
            <span className="caption">
              {value === null
                ? labels.all
                : `${range(value)} · ${periodDays(value)} ${labels.days}`}
            </span>
            <button
              type="button"
              onClick={() => choose('today')}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'var(--accent)',
                fontSize: 'var(--fs)',
                fontWeight: 'var(--fw-medium)',
              }}
            >
              {labels.reset}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function monthOf(day: Date): Date {
  return new Date(day.getFullYear(), day.getMonth(), 1);
}

function arrowStyle(disabled: boolean): CSSProperties {
  return {
    width: 'var(--cal-cell)',
    height: 'var(--cal-cell)',
    border: 'none',
    borderRadius: 'var(--r-sm)',
    background: 'transparent',
    fontSize: 'var(--fs-heading)',
    color: disabled ? 'var(--text-muted)' : 'var(--text)',
    opacity: disabled ? 'var(--muted-opacity)' : 1,
  };
}

function dayStyle({
  edge,
  inside,
  inMonth,
  future,
  today,
}: {
  readonly edge: boolean;
  readonly inside: boolean;
  readonly inMonth: boolean;
  readonly future: boolean;
  readonly today: boolean;
}): CSSProperties {
  return {
    position: 'relative',
    width: 'var(--cal-cell)',
    height: 'var(--cal-cell)',
    border: 'none',
    borderRadius: 'var(--r-sm)',
    fontSize: 'var(--fs)',
    fontWeight: edge || today ? 'var(--fw-semibold)' : 'var(--fw-regular)',
    background: edge ? 'var(--accent)' : inside ? 'var(--accent-soft)' : 'transparent',
    color: edge
      ? 'var(--text-on-accent)'
      : inside
        ? 'var(--accent-ink)'
        : inMonth && !future
          ? 'var(--text)'
          : 'var(--text-muted)',
    opacity: !edge && !inside && (future || !inMonth) ? 'var(--muted-opacity)' : 1,
    textDecoration: today && !edge ? 'underline' : 'none',
  };
}

function CalendarIcon(): ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="2" />
      <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
